import React, { FC } from 'react'
import styled from 'styled-components'
import {
  Box,
  Button,
  TextBody,
  useSteppedProcess,
  ButtonGroup,
  useModalContext,
  get,
  CardSelectionGroup,
  CardSelectionCard,
} from '@ally/metronome-ui'
import { Forms, Form, useFormsContext } from '@ally/forms'
import {
  StepTwoFormValues,
  stepTwoFormInitialValues,
} from './StepTwoAddNewBene'
import { KeyValuePairObj, fireErrorTagging } from '../../../utils/tagging'

export interface FormValues {
  beneficiaryClassification: string
}

const StyledTextBodyErrorColor = styled(TextBody)`
  color: ${get.colors('error')};
`

const StyledCardSelectionGroup = styled(CardSelectionGroup)`
  text-align: left;
`
const StyledCardSelectionCard = styled(CardSelectionCard)`
  ${get.media.up('md')} {
    width: 320px;
  }
  flex-direction: row;
`

export const StepOneForm: FC<{
  updateStateStepTwo: (valuesToUpdate: Partial<StepTwoFormValues>) => void
  updateStateStepThree: () => void
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
  setDisplayOtherFdicModalContent?: React.Dispatch<
    React.SetStateAction<boolean>
  >
}> = ({
  updateStateStepTwo,
  updateStateStepThree,
  setStepTwoFormErrorCode,
  setDisplayOtherFdicModalContent,
}) => {
  const {
    formId,
    isSubmitting,
    values,
    handleSubmit,
    formError,
    formErrorRef,
    setFormError,
    setFieldValue,
  } = useFormsContext()
  const { setOpen } = useModalContext()

  // Low value test
  // istanbul ignore next
  const handleChange = (cardId: string): void => {
    setFormError('')
    setFieldValue('beneficiaryClassification', cardId)
    setStepTwoFormErrorCode('')
    updateStateStepTwo({
      ...stepTwoFormInitialValues,
      beneficiaryClassification: values.beneficiaryClassification,
    })
    updateStateStepThree()
  }

  const onSubmit = (): void => {
    // Add test case with functionality integration
    // istanbul ignore next
    if (values.beneficiaryClassification) {
      setFormError('')
      handleSubmit()
    } else {
      setFormError('Make a selection')
      fireErrorTagging({
        beneficiaryClassification: 'Make a selection',
      } as KeyValuePairObj)
    }
  }

  return (
    <Form id={formId}>
      <StyledCardSelectionGroup
        id="beneficiary-classification-group"
        align="stacked"
        variant="single-select"
        ariaLabelledby="beneficiary-classification-group-label"
        onSelection={
          /* istanbul ignore next */ (cardId: any): void => {
            handleChange(cardId as string)
          }
        }
        defaultSelection={values.beneficiaryClassification}
        data-testid="beneficiary-classification-group"
      >
        <StyledCardSelectionCard
          cardId="individual"
          title="Individual"
          data-testid="individual"
        />
        <StyledCardSelectionCard
          cardId="charity"
          title="Non-profit or charity"
          data-testid="charity"
        />
        <StyledCardSelectionCard
          cardId="trust"
          title="Trust"
          data-testid="trust"
        />
        <StyledCardSelectionCard
          cardId="other"
          title="Other"
          data-testid="other"
        />
      </StyledCardSelectionGroup>
      {values.beneficiaryClassification === 'other' && (
        <Box marginY="md">
          <TextBody tag="p" size="sm">
            Expanded FDIC coverage may apply to individuals, non-profits and
            charities. Upon your death, Ally Bank pays your named beneficiaries
            based on your account ownership; however, you should know that
            &quot;other&quot; beneficiaries may have limited or no FDIC
            coverage. Consult the{' '}
            <Button
              text="FDIC"
              onClick={(): void => {
                if (setDisplayOtherFdicModalContent) {
                  setDisplayOtherFdicModalContent(true)
                }
              }}
              variant="link"
              data-testid="fdic-link"
            />{' '}
            for more information.
          </TextBody>
        </Box>
      )}
      {formError && (
        <Box mt="md">
          <StyledTextBodyErrorColor
            tag="p"
            size="sm"
            focusOnMount
            ref={formErrorRef}
          >
            {formError}
          </StyledTextBodyErrorColor>
        </Box>
      )}
      <Box marginY="md">
        <ButtonGroup>
          <Button
            allytmln="continue from step one"
            text="Continue"
            disabled={isSubmitting}
            onClick={onSubmit}
          />
          <Button
            allytmln="Cancel Add Beneficiary"
            text="Cancel"
            onClick={
              // istanbul ignore next
              (): void => setOpen(false)
            }
            variant="link"
          />
        </ButtonGroup>
      </Box>
    </Form>
  )
}

export const StepOneAddNewBene: FC<{
  existingBeneficiaryClassification: string
  setBeneficiaryClassification: React.Dispatch<React.SetStateAction<string>>
  updateStateStepTwo: (valuesToUpdate: Partial<StepTwoFormValues>) => void
  updateStateStepThree: () => void
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
  setDisplayOtherFdicModalContent?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  classificationToOther?: boolean
}> = ({
  existingBeneficiaryClassification,
  setBeneficiaryClassification,
  updateStateStepTwo,
  updateStateStepThree,
  setStepTwoFormErrorCode,
  setDisplayOtherFdicModalContent,
  classificationToOther,
}) => {
  const { setNextStepActive } = useSteppedProcess()

  // Add test case with functionality integration
  // istanbul ignore next
  const handleSubmit = (values: FormValues): void => {
    setBeneficiaryClassification(values.beneficiaryClassification)
    setNextStepActive()
  }

  return (
    <Forms
      formId="beneficiary-classification-form"
      initialValues={{
        beneficiaryClassification: classificationToOther
          ? // istanbul ignore next
            'other'
          : existingBeneficiaryClassification,
      }}
      onSubmit={handleSubmit}
    >
      <StepOneForm
        updateStateStepTwo={updateStateStepTwo}
        updateStateStepThree={updateStateStepThree}
        setStepTwoFormErrorCode={setStepTwoFormErrorCode}
        setDisplayOtherFdicModalContent={setDisplayOtherFdicModalContent}
      />
    </Forms>
  )
}
